// JsonEditor.tsx
import { useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { keymap, EditorView } from "@codemirror/view";
import Editor from "@monaco-editor/react";
import React from "react";

/** quick-n-dirty JSON.stringify formatter */
const pretty = (view: EditorView) => {
  try {
    const raw = view.state.doc.toString();
    const formatted = JSON.stringify(JSON.parse(raw), null, 2); // 2-space indent
    view.dispatch({ changes: { from: 0, to: raw.length, insert: formatted } });
  } catch (_) {
    /* ignore if the JSON is invalid */
  }
  return true; // tell CodeMirror we handled the key
};

interface JsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  codemirror?: boolean;
}

export default function JsonEditor(props: JsonEditorProps) {
  const [value, setValue] = useState(props.value);
  const { codemirror = false } = props;
  
  // Update local state when props change (e.g., when switching between different configs)
  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);
  
  const handleChange = (val: string) => {
    setValue(val);
    if (props.onChange) {
      props.onChange(val);
    }
  };

  const handleMonacoChange = (val: string | undefined) => {
    const newValue = val || '';
    setValue(newValue);
    if (props.onChange) {
      props.onChange(newValue);
    }
  };


  if (codemirror) {
    return (
      <CodeMirror
        value={value}
        height="100%"
        extensions={[
          json(),
          keymap.of([{ key: "Shift-Alt-F", run: pretty }]), // VS Code shortcut
        ]}
        onChange={handleChange}
      />
    );
  }

  return (
    <div style={{ 
      height: 'calc(100vh - 250px)', 
      minHeight: '400px', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      border: '1px solid #30363d'
    }}>
      <Editor
        height="100%"
        width="100%"
        language="json"
        value={value}
        onChange={handleMonacoChange}
        options={{
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          automaticLayout: true,
          wordWrap: 'on',
          formatOnPaste: true,
          formatOnType: true,
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto'
          },
          overviewRulerLanes: 0,
          lineNumbers: 'on',
          glyphMargin: false,
          folding: true,
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 3,
          renderLineHighlight: 'gutter',
          contextmenu: true,
          selectOnLineNumbers: true,
          fontSize: 14,
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace'
        }}
        theme="vs-dark"
      />
    </div>
  );
}
